import React, { useEffect, useState } from "react";
import { useNavigate } from 'react-router-dom';
import './Blog.css';

const BlogPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const blogsPerPage = 7; // Number of blogs to load per page
  const navigate = useNavigate();

  useEffect(() => {
  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/blogs?page=${page}&limit=${blogsPerPage}`);
      if (!response.ok) {
        throw new Error('Failed to fetch blogs');
      }
      const data = await response.json();
      
      // Check if data is formatted with pagination metadata or just an array
      if (data.blogs && Array.isArray(data.blogs)) {
        setTotalPages(data.pages || 1);
        setHasMore(page < data.pages);
        
        if (page === 1) {
          // If it's the first page, replace all blogs
          setBlogs(data.blogs);
        } else {
          // If it's a subsequent page, append blogs
          setBlogs(prevBlogs => [...prevBlogs, ...data.blogs]);
        }
      } else if (Array.isArray(data)) {
        // Handle case where API returns just an array
        setBlogs(prevBlogs => page === 1 ? data : [...prevBlogs, ...data]);
        setHasMore(data.length === blogsPerPage);
      } else {
        setBlogs([]);
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching blogs:", error);
    } finally {
      setLoading(false);
    }
  };

  fetchBlogs();
}, [page]);


  const truncateDescription = (html, maxLength) => {
    const div = document.createElement('div');
    div.innerHTML = html;
    const text = div.textContent || div.innerText || '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  const handleBlogClick = (id) => {
    navigate(`/blog/${id}`);
  };
  
  const loadMoreBlogs = () => {
    if (hasMore && !loading) {
      setPage(prevPage => prevPage + 1);
    }
  };

  if (loading && page === 1) {
    return (
      <div className="blog">
        <div className="container">
          <div className="loading-spinner">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="blog">
      <div className="blog__container container">
        <div className="blog__header">
          <h1 className="blog__title">Blog</h1>
        </div>

        {/* Featured Blog - Show the newest blog */}
        {blogs.length > 0 && (
          <div className="blog__featured">
            <div 
              className="blog-card blog-card--featured"
              onClick={() => handleBlogClick(blogs[0]._id)}
            >
              <div className="blog-card__row">
                <div className="blog-card__image-wrapper">
                  {blogs[0].image && (
                    <img
                      src={`${process.env.REACT_APP_API_BASE_URL}/${blogs[0].image}`}
                      alt={blogs[0].title}
                      className="blog-card__image blog-card__image--featured"
                      loading="lazy"
                    />
                  )}
                </div>
                <div className="blog-card__content blog-card__content--featured">
                  <h2 className="blog-card__title blog-card__title--featured">{blogs[0].title}</h2>
                  <p className="blog-card__description blog-card__description--featured">
                    {truncateDescription(blogs[0].description, 250)}
                  </p>
                  <span className="blog-card__category">{blogs[0].category}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Regular Blog Grid */}
        <div className="blog__grid">
          {blogs.slice(1).map((blog) => (
            <div className="blog__grid-item" key={blog._id}>
              <div 
                className="blog-card"
                onClick={() => handleBlogClick(blog._id)}
              >
                {blog.image && (
                  <img
                    src={`${process.env.REACT_APP_API_BASE_URL}/${blog.image}`}
                    alt={blog.title}
                    className="blog-card__image"
                    loading="lazy"
                  />
                )}
                <div className="blog-card__content">
                  <h3 className="blog-card__title">{blog.title}</h3>
                  <p className="blog-card__description">
                    {truncateDescription(blog.description, 150)}
                  </p>
                  <span className="blog-card__category">{blog.category}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
        
        {/* Load More Button */}
        {blogs.length > 0 && (
          <div className="blog__pagination">
            {loading && page > 1 ? (
              <div className="blog__loading">Loading more...</div>
            ) : hasMore ? (
              <button 
                className="blog__load-more-btn" 
                onClick={loadMoreBlogs}
                disabled={loading}
              >
                Load More
              </button>
            ) : (
              <p className="blog__end-message">No more blogs to load</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
export default BlogPage;